import { PageHeader, Section, DividerGold } from "@/components/layout/Section";
import { IMAGES, APOSTLE_IMAGE } from "@/constants/branding";
import { Crown, MapPin, Users, Home as HomeIcon } from "lucide-react";
import { motion } from "framer-motion";

const NETWORKS = [
  {
    icon: MapPin,
    name: "Kumasi Network",
    presbyter: "Lady Jane",
    role: "Presbyter",
    description:
      "The Ashanti-region gathering of CLBC — a Spirit-filled community raising disciples, ministering the Word, and demonstrating the love of Christ across Kumasi.",
    accent: "from-royal to-royal-700",
  },
  {
    icon: MapPin,
    name: "Accra Network",
    presbyter: "Sir Benjamin",
    role: "Presbyter",
    description:
      "The capital-city expression of CLBC — carrying the vision of Christ Love Breed into the heart of Accra through worship, teaching, and outreach.",
    accent: "from-royal-700 to-royal-800",
  },
  {
    icon: HomeIcon,
    name: "Wellspring Family",
    presbyter: "Lady Aboraa",
    role: "Family Head",
    description:
      "A family within the family — a place of nurture, prayer, and Kingdom fellowship where members are pastored, discipled, and equipped for destiny.",
    accent: "from-gold-500 to-gold",
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHeader
        kicker="About Christ Love Breed"
        title="A house of the Spirit, a family of love, a launching pad for destinies."
        subtitle="We are a Christ-centered, Spirit-empowered church committed to raising disciples who reflect the heart of the Father."
      />

      <Section>
        <div className="grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-6">
            <div className="aspect-[4/5] rounded-[2.5rem] overflow-hidden shadow-xl">
              <img src={IMAGES.community} alt="CLBC community" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="kicker mb-4">Our Story</div>
            <h2 className="font-serif text-4xl text-royal-800 tracking-tight leading-tight">
              Born of the Spirit. Built on the Word. Bound by love.
            </h2>
            <div className="mt-6 space-y-5 text-slate-600 leading-relaxed">
              <p>
                Christ Love Breed Church (CLBC) was raised as a prophetic sanctuary of the Holy Spirit — a place where the presence of God is honoured, His Word is faithfully taught, and His people are equipped to walk in destiny.
              </p>
              <p>
                Under the apostolic leadership of Apostle Percy Konotey, CLBC exists to demonstrate the love of Christ in tangible ways — through worship, discipleship, community, and unashamed proclamation of the Gospel.
              </p>
              <p>
                Whether you're seeking, wounded, hungry, or thriving — there is a place for you at this table.
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* Networks & Structure */}
      <Section className="bg-slate-50/70 rounded-[3rem] mx-4" testId="about-networks">
        <div className="text-center max-w-2xl mx-auto">
          <div className="kicker justify-center mb-4">Our Networks</div>
          <h2 className="font-serif text-4xl sm:text-5xl text-royal-800">One church. Many families.</h2>
          <p className="mt-6 text-slate-600 leading-relaxed">
            Christ Love Breed Church is structured through regional Networks and Families — each led by shepherds who love God, love people, and carry the DNA of the house.
          </p>
          <DividerGold className="mt-8" />
        </div>

        {/* General Overseer — top of structure */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mt-16 max-w-4xl mx-auto"
          data-testid="about-general-overseer"
        >
          <div className="relative rounded-[2rem] overflow-hidden bg-royal-800 text-white shadow-2xl">
            <div className="absolute inset-0 opacity-30">
              <img src={APOSTLE_IMAGE} alt="" className="w-full h-full object-cover" />
            </div>
            <div className="absolute inset-0 bg-gradient-to-r from-royal-800 via-royal-800/85 to-royal-800/20" />
            <div className="relative grid md:grid-cols-5 gap-8 items-center p-8 md:p-12">
              <div className="md:col-span-3">
                <div className="inline-flex items-center gap-2 text-gold text-xs font-semibold tracking-[0.25em] uppercase">
                  <Crown className="w-4 h-4" /> General Overseer
                </div>
                <h3 className="mt-4 font-serif text-4xl sm:text-5xl leading-tight">Apostle Percy Konotey</h3>
                <p className="mt-5 text-white/80 leading-relaxed">
                  Founder and spiritual father of Christ Love Breed Church — the apostolic covering under whom all Networks and Families of CLBC are gathered, taught, and sent.
                </p>
              </div>
              <div className="md:col-span-2 flex justify-center md:justify-end">
                <div className="relative w-40 h-52 md:w-48 md:h-60 rounded-3xl overflow-hidden border-4 border-gold/60 shadow-xl">
                  <img src={APOSTLE_IMAGE} alt="Apostle Percy Konotey" className="w-full h-full object-cover" />
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Networks + Wellspring Family */}
        <div className="mt-10 grid md:grid-cols-3 gap-6">
          {NETWORKS.map((n, i) => (
            <motion.article
              key={n.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.65, delay: i * 0.1 }}
              className="relative card-soft p-8 overflow-hidden"
              data-testid={`about-network-${i}`}
            >
              <div className={`absolute -top-16 -right-16 w-40 h-40 rounded-full bg-gradient-to-br ${n.accent} opacity-15 blur-2xl`} />
              <div className="relative">
                <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${n.accent} text-white flex items-center justify-center shadow-md`}>
                  <n.icon className="w-5 h-5" />
                </div>
                <h3 className="mt-6 font-serif text-2xl text-royal">{n.name}</h3>
                <div className="mt-2 text-xs uppercase tracking-widest text-gold-500">
                  {n.role} · {n.presbyter}
                </div>
                <p className="mt-5 text-slate-600 leading-relaxed text-sm">{n.description}</p>
                <div className="mt-6 pt-4 border-t border-slate-100 flex items-center gap-2 text-xs text-slate-500">
                  <Users className="w-3.5 h-3.5 text-gold" />
                  <span>Part of Christ Love Breed Church</span>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </Section>

      {/* Core Beliefs */}
      <Section>
        <div className="text-center max-w-2xl mx-auto">
          <div className="kicker justify-center mb-4">What we believe</div>
          <h2 className="font-serif text-4xl text-royal-800">Our Core Beliefs</h2>
          <DividerGold className="mt-6" />
        </div>
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { t: "The Bible", d: "The 66 books of the Holy Bible are the inspired, infallible Word of God — our final authority for faith and life." },
            { t: "The Trinity", d: "One God eternally existing in three Persons: Father, Son, and Holy Spirit." },
            { t: "Jesus Christ", d: "Fully God and fully man — crucified for our sins, risen bodily, and coming again in glory." },
            { t: "Salvation", d: "By grace through faith in Jesus alone — a free gift received through repentance and confession." },
            { t: "Holy Spirit", d: "The empowering presence of God, indwelling every believer and gifting the Church for ministry." },
            { t: "The Church", d: "The Body of Christ — a family of worship, discipleship, and Kingdom advancement." },
          ].map((b) => (
            <div key={b.t} className="card-soft p-7">
              <h3 className="font-serif text-2xl text-royal">{b.t}</h3>
              <p className="mt-3 text-slate-600 text-sm leading-relaxed">{b.d}</p>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}
